import { ImageService } from '@billx/api';
import type { Image, ImageFilters } from '@billx/types';
import { ensureError } from '@billx/utils';
import { create } from 'zustand';

interface ImageState {
  images: Image[];
  isLoading: boolean;
  error: string | null;
  uploadProgress: number;
  isUploading: boolean;
}

interface ImageActions {
  fetchImagesByBusinessId: (filters: ImageFilters) => Promise<void>;
  uploadImages: (
    files: File[],
    businessId: string,
    entity?: string,
    usecase?: string
  ) => Promise<Image[]>;
  deleteImage: (imageId: string) => Promise<void>;
  incrementImageUsage: (imageId: string) => Promise<void>;
  decrementImageUsage: (imageId: string) => Promise<void>;
  clearError: () => void;
  resetUploadProgress: () => void;
}

type ImageStore = ImageState & ImageActions;

export const useImageStore = create<ImageStore>((set) => ({
  // State
  images: [],
  isLoading: false,
  error: null,
  uploadProgress: 0,
  isUploading: false,

  // Actions
  fetchImagesByBusinessId: async (filters: ImageFilters) => {
    set({ isLoading: true, error: null });
    try {
      const result = await ImageService.getImagesByBusinessId(filters);
      if (result.success) {
        set({ images: result.data, isLoading: false });
      } else {
        set({
          error: result.message || 'Failed to fetch images',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: ensureError(error, 'Failed to fetch images').message,
        isLoading: false,
      });
    }
  },

  uploadImages: async (
    files: File[],
    businessId: string,
    entity: string = 'businesses',
    usecase: string = 'menu'
  ) => {
    set({ isUploading: true, uploadProgress: 0, error: null });

    try {
      const totalFiles = files.length;
      const uploadedImages: Image[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Generate unique image ID
        const imageId = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Upload image to storage
        const uploadResult = await ImageService.uploadImage();
        if (!uploadResult.success) {
          console.error(`Failed to upload ${file.name}:`, uploadResult.message);
          continue;
        }

        // Get image dimensions

        // Create image document
        const fileExtension = file.name.split('.').pop() || 'jpg';
        const fileName = `${imageId}.${fileExtension}`;

        const imageData = {
          businessId,
          usecase,
          originalName: file.name,
          fileName,
          filePath: `${entity}/${businessId}/${usecase}/${fileName}`,
          fileSize: uploadResult.data.fileSize,
          mimeType: file.type,
          width: 20,
          height: 20,
        };

        const createResult = await ImageService.createImage(imageData);
        if (createResult.success) {
          uploadedImages.push(createResult.data);
        }

        // Update progress
        const progress = ((i + 1) / totalFiles) * 100;
        set({ uploadProgress: progress });
      }

      // Add new images to existing images
      set((state) => ({
        images: [...uploadedImages, ...state.images],
        isUploading: false,
        uploadProgress: 100,
      }));

      return uploadedImages;
    } catch (error) {
      set({
        error: ensureError(error, 'Failed to upload images').message,
        isUploading: false,
        uploadProgress: 0,
      });
      return [];
    }
  },

  deleteImage: async (imageId: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await ImageService.deleteImage(imageId);
      if (result.success) {
        set((state) => ({
          images: state.images.filter((image) => image.id !== imageId),
          isLoading: false,
        }));
      } else {
        set({
          error: result.message || 'Failed to delete image',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: ensureError(error, 'Failed to delete image').message,
        isLoading: false,
      });
    }
  },

  incrementImageUsage: async (imageId: string) => {
    try {
      const result = await ImageService.incrementImageUsage(imageId);
      if (result.success) {
        // Update local state optimistically
        set((state) => ({
          images: state.images.map((image) =>
            image.id === imageId
              ? { ...image, usageCount: image.usageCount + 1 }
              : image
          ),
        }));
      }
    } catch (error) {
      console.error('Error incrementing image usage:', error);
    }
  },

  decrementImageUsage: async (imageId: string) => {
    try {
      const result = await ImageService.decrementImageUsage(imageId);
      if (result.success) {
        // Update local state optimistically
        set((state) => ({
          images: state.images.map((image) =>
            image.id === imageId
              ? { ...image, usageCount: Math.max(0, image.usageCount - 1) }
              : image
          ),
        }));
      }
    } catch (error) {
      console.error('Error decrementing image usage:', error);
    }
  },

  clearError: () => set({ error: null }),
  resetUploadProgress: () => set({ uploadProgress: 0 }),
}));
